import { debounce, Grid, Typography } from '@mui/material';

import { ReactNode, useEffect, useState } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import ButtonComp from 'common/components/Button/ButtonComp';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import {
	APP_GENERAL_ERROR_MSG,
	GENERIC_ERROR_MSG,
} from 'common/constants/TextContent';
import { WORKFLOW_TEXT_CONSTANTS } from 'common/constants/WorkflowConstants';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import {
	addUsersToStateList,
	toSentenceCase,
} from 'common/utils/utilFunctions';

import { AutoComplete } from 'features/common/AutoCompleteSelect/AutoComplete';
import { getUsersIdpList } from 'features/Settings/api';
import { IUserSelectListOptions } from 'features/Settings/api/type-def';

import UsersTable from '../UsersListTable/UserList';

import styles from './UsersListSelection.module.scss';

interface IUsersListSelectionProps {
	onUserListItemSelect: (data: IUserSelectListOptions[]) => void;
	existingUsersData: IUserSelectListOptions[];
	hideLabel?: boolean;
	title?: string | ReactNode;
	actionType?: string;
	hideLoggedInUser?: boolean;
	sharedBy?: string;
}
const defaultProps = {
	title: 'Users',
	hideLoggedInUser: false,
};

const { OK } = HTTP_STATUS_CODES;

const { ADD_USERS } = WORKFLOW_TEXT_CONSTANTS;

const UsersListSelection = (props: IUsersListSelectionProps) => {
	const {
		onUserListItemSelect,
		existingUsersData,
		hideLabel,
		title = 'Users',
		actionType = 'create',
		hideLoggedInUser = false,
		sharedBy,
	} = props;
	const dispatch = useDispatch();

	const [usersListOptions, setUsersListOptions] = useState<
		IUserSelectListOptions[] | any
	>([]);
	const [selectedUsers, setSelectedUsers] = useState<IUserSelectListOptions[]>(
		[],
	);
	const [isSearchApiLoading, setSearchLoading] = useState<boolean>(false);
	const [selectedUsersGridData, setSelectedUsersGridData] = useState<
		IUserSelectListOptions[]
	>([]);
	const [updatedExistingData, setUpdatedExistingData] = useState<
		IUserSelectListOptions[]
	>([]);
	const [isToastOpen, setToastOpen] = useState<boolean>(false);
	const { message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	const onUserListSelect = (data: IUserSelectListOptions[]) => {
		setSelectedUsers(data);
	};

	const addUsersToList = (data: any) => {
		const allUsersList = addUsersToStateList(data);
		const excludeLoggedInuser = allUsersList?.filter(
			(user: any) => user.id !== loggedInUser?.email,
		);
		const excludingCreatedLoggedInUser = excludeLoggedInuser?.filter(
			(user: any) => user.id !== sharedBy,
		);
		const finalUsersList = hideLoggedInUser
			? excludingCreatedLoggedInUser
			: allUsersList;
		if (updatedExistingData?.length > 0) {
			const ids = updatedExistingData.map((c) => c.email);
			const users = finalUsersList.map((ele: any) => {
				if (ids.indexOf(ele?.id) !== -1) {
					ele.isExisting = true;
				}
				return ele;
			});
			setUsersListOptions(users);
		} else {
			setUsersListOptions(finalUsersList);
		}
	};

	const onSearchTextChange = async (data: string) => {
		if (data && data?.length > 2) {
			setSearchLoading(true);
			try {
				const resp = await getUsersIdpList(data);
				if (resp?.statusCode === OK) {
					addUsersToList(resp?.responseBody);
					setSearchLoading(false);
					setSelectedUsers([]);
				} else {
					setSelectedUsers([]);
					setSearchLoading(false);
					dispatch(
						setToastMessage({
							message: APP_GENERAL_ERROR_MSG,
							severity: 'error',
							isToastOpen: true,
						}),
					);
				}
			} catch (e) {
				setSearchLoading(false);
				setToastOpen(true);
				dispatch(
					setToastMessage({
						message: GENERIC_ERROR_MSG,
						severity: 'error',
						isToastOpen: true,
					}),
				);
			}
		}
	};
	const debouncedSearchHandler = debounce(onSearchTextChange, 300);
	const handleDelete = (id: string) => {
		const allUsers = selectedUsersGridData?.filter(
			(user: any) => user.id !== id,
		);
		if (updatedExistingData?.length > 0) {
			const updatedUsersExistingData = updatedExistingData?.filter(
				(user: any) => user.id !== id,
			);
			setUpdatedExistingData([...updatedUsersExistingData]);
		}

		setSelectedUsersGridData(allUsers);
		onUserListItemSelect(allUsers);
		addUsersToList([]);
	};
	const addQueueSelectedUsers = () => {
		if (selectedUsers.length > 0) {
			if (selectedUsersGridData?.length > 0) {
				const combinedArray = getUniqueList();
				setSelectedUsersGridData(combinedArray);
				onUserListItemSelect(combinedArray);
				setSelectedUsers([]);
			} else {
				setSelectedUsersGridData(selectedUsers);
				onUserListItemSelect(selectedUsers);
				setSelectedUsers([]);
			}
		} else if (actionType === 'create') {
			setSelectedUsersGridData([]);
			onUserListItemSelect([]);
		}
	};

	const getUniqueList = () => {
		const combinedArray: IUserSelectListOptions[] = [
			...selectedUsers,
			...updatedExistingData,
		].reduce((accumulator: any, current: IUserSelectListOptions) => {
			const existingItem = accumulator.find(
				(item: IUserSelectListOptions) => item?.email === current?.email,
			);
			if (!existingItem) {
				accumulator.push(current);
			}

			return accumulator;
		}, []);

		return combinedArray;
	};

	const handleClose = () => {
		setToastOpen(false);
	};
	useEffect(() => {
		if (existingUsersData?.length > 0) {
			const existingData = addUsersToStateList(existingUsersData, true);
			setSelectedUsersGridData([...existingData]);
			setSelectedUsers([...existingData]);
			setUpdatedExistingData([...existingData]);
		} else {
			setSelectedUsersGridData([]);
			setSelectedUsers([]);
			setUpdatedExistingData([]);
		}
	}, [existingUsersData]);

	return (
		<Grid
			container
			className={styles['userslist-selection-grid']}
			data-testid="createqueue-users-area"
		>
			{!hideLabel && (
				<Grid item>
					<Typography className={styles['userslist-selection-header']}>
						{title}
					</Typography>
				</Grid>
			)}
			<Grid
				container
				direction="row"
				className={styles['userslist-selection-row']}
			>
				<Grid item xs={8} sm={8} md={8} xl={9} lg={9} p={1}>
					<div data-testid="users-list">
						<AutoComplete
							data={usersListOptions}
							onUserSelect={onUserListSelect}
							type="email"
							key="email"
							showLabel
							displayLabel={toSentenceCase('Search for a User')}
							onTextChange={debouncedSearchHandler}
							apiSearch
							selectedData={selectedUsersGridData}
							isSearchLoading={isSearchApiLoading}
						/>
					</div>
				</Grid>
				<Grid item xs={4} sm={4} md={4} xl={3} lg={3} p={1.6}>
					<ButtonComp
						btnType="primary"
						data-testid="createqueue-add-users"
						compClass={styles['userslist-selection-add-users-btn']}
						clickHandler={addQueueSelectedUsers}
						disabled={
							selectedUsers?.length === 0 && selectedUsersGridData.length === 0
						}
					>
						{toSentenceCase(ADD_USERS)}
					</ButtonComp>
				</Grid>
			</Grid>
			<Grid item xs={12} sm={12} md={12} xl={12} lg={12}>
				<Typography className={styles['userslist-selection-note-text']}>
					<span className={styles['userslist-selection-note-weight']}>
						Note
					</span>
					<span className={styles['userslist-selection-note-sub-text']}>
						: Please choose the users you wish to add by searching and selecting
						the checkboxes.
					</span>
				</Typography>
			</Grid>
			<Grid
				item
				xs={12}
				sm={12}
				md={12}
				xl={12}
				lg={12}
				p={1}
				className={styles['userslist-selection-users-list']}
				data-testid="createqueue-selected-users"
			>
				<UsersTable
					sharedBy={sharedBy}
					usersOptions={selectedUsersGridData}
					handleDelete={handleDelete}
				/>
			</Grid>
			<AlertComponent
				open={isToastOpen}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleClose}
				vertical="top"
				horizontal="right"
			/>
		</Grid>
	);
};
export default UsersListSelection;

UsersListSelection.defaultProps = defaultProps;
